import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime, CLP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PushToggle } from "@/components/PushToggle";
import { FavoriteMaestrosList } from "@/components/FavoriteMaestrosList";
import { Loader2, UserRound, Users, Sparkles, Flame, Megaphone, Compass, CalendarDays, ImageOff, BellOff } from "lucide-react";
import { useLive } from "@/lib/useLive";

const FILTERS = [
  { id: "all", t: "Todo", f: () => true },
  { id: "new", t: "Nuevas", f: (s) => s.is_new },
  { id: "low", t: "Últimos cupos", f: (s) => s.low_spots },
];

const Avatar = ({ src, name, size = "w-14 h-14" }) => (
  <div className={`${size} rounded-full overflow-hidden bg-muted grid place-items-center shrink-0 ring-2 ring-primary/30`}>
    {src ? <img src={fileUrl(src)} alt={name} className="w-full h-full object-cover" /> : <UserRound className="w-6 h-6 text-muted-foreground" />}
  </div>
);

function FollowedStrip({ maestros }) {
  return (
    <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x" data-testid="followed-strip">
      {maestros.map((m) => (
        <Link key={m.id} to={`/maestro/${m.id}`} className="snap-start w-20 shrink-0 text-center group" data-testid={`followed-${m.id}`}>
          <div className="relative w-14 h-14 mx-auto transition-transform group-hover:scale-105">
            <Avatar src={m.avatar} name={m.name} />
            {m.upcoming > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center">{m.upcoming}</span>}
            {m.muted && <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-background border grid place-items-center" title="Avisos silenciados" data-testid={`followed-muted-${m.id}`}><BellOff className="w-3 h-3 text-muted-foreground" /></span>}
          </div>
          <p className="text-[11px] font-semibold mt-1.5 line-clamp-2 leading-tight">{m.name}</p>
        </Link>
      ))}
    </div>
  );
}

function PostCard({ p }) {
  return (
    <Card className="p-4 border-l-4 border-l-sun" data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-2.5">
        <Avatar src={p.avatar} name={p.business_name} size="w-9 h-9" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold truncate">{p.business_name}</p>
          <p className="text-[11px] text-muted-foreground">{formatDateTime(p.created_at)}</p>
        </div>
        <Megaphone className="w-4 h-4 text-sun shrink-0" />
      </div>
      <p className="text-sm mt-2.5 whitespace-pre-line break-words">{p.message}</p>
      {p.workshop_id && <Button asChild size="sm" variant="outline" className="mt-3 rounded-full" data-testid={`post-go-${p.id}`}><Link to={`/taller/${p.workshop_id}`}>Ver {p.workshop_name}</Link></Button>}
    </Card>
  );
}

function SessionCard({ s, maestro }) {
  return (
    <Link to={`/taller/${s.workshop_id}`} data-testid={`feed-session-${s.session_id}`}>
      <Card className="p-3 flex gap-3 hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform]">
        <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted grid place-items-center shrink-0">
          {s.image ? <img src={fileUrl(s.image)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <ImageOff className="w-5 h-5 text-muted-foreground" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-1 mb-1">
            {s.is_new && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-primary/10 text-primary"><Sparkles className="w-3 h-3" />Nueva</span>}
            {s.low_spots && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-coral/15 text-coral" data-testid={`feed-low-${s.session_id}`}><Flame className="w-3 h-3" />Quedan {s.spots_left}</span>}
            {s.spots_left === 0 && <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-muted text-muted-foreground">Sin cupos</span>}
          </div>
          <p className="font-bold text-sm line-clamp-1">{s.workshop_name}</p>
          <p className="text-xs text-primary truncate">{maestro?.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{formatDateTime(s.starts_at)}</p>
        </div>
        <p className="text-sm font-extrabold shrink-0 self-center">{s.price_clp ? CLP(s.price_clp) : "Gratis"}</p>
      </Card>
    </Link>
  );
}

function Feed({ data }) {
  const [flt, setFlt] = useState("all");
  const [allPosts, setAllPosts] = useState(false);
  const byId = Object.fromEntries(data.maestros.map((m) => [m.id, m]));
  const rows = data.sessions.filter(FILTERS.find((f) => f.id === flt).f);
  return (
    <div className="space-y-6">
      <FollowedStrip maestros={data.maestros} />
      {data.posts.length > 0 && <div className="space-y-3" data-testid="feed-posts">{data.posts.slice(0, allPosts ? 30 : 3).map((p) => <PostCard key={p.id} p={p} />)}
        {data.posts.length > 3 && <Button variant="ghost" size="sm" className="w-full" onClick={() => setAllPosts((v) => !v)} data-testid="feed-posts-more">{allPosts ? "Ver menos avisos" : `Ver los ${data.posts.length} avisos`}</Button>}</div>}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-base md:text-lg font-bold">Próximas sesiones</h2>
          <div className="flex gap-1.5">
            {FILTERS.map((f) => <button key={f.id} type="button" onClick={() => setFlt(f.id)} data-testid={`feed-filter-${f.id}`}
              className={`h-7 px-3 rounded-full text-xs font-semibold border transition-colors ${flt === f.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>{f.t}</button>)}
          </div>
        </div>
        <div className="space-y-2.5" data-testid="feed-sessions">
          {rows.map((s) => <SessionCard key={s.session_id} s={s} maestro={byId[s.business_id]} />)}
          {rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-8" data-testid="feed-sessions-empty">Tus Maestros no tienen sesiones {flt === "all" ? "próximas" : "en este filtro"} por ahora.</p>}
        </div>
      </div>
    </div>
  );
}

export default function MisMaestros() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [manage, setManage] = useState(false);
  useEffect(() => {
    if (user?.role === "customer") api.get("/following/feed").then((r) => setData(r.data)).catch(() => setData({ maestros: [], sessions: [], posts: [] }));
  }, [user?.role, manage]);
  useLive(() => api.get("/following/feed").then((r) => setData(r.data)), 30000, user?.role === "customer" && !manage);

  if (user && user.role !== "customer") return <Navigate to="/app" replace />;
  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="mis-maestros-guest">
      <Users className="w-10 h-10 mx-auto text-primary mb-3" />
      <p className="font-bold text-lg">Sigue a tus Maestros favoritos</p>
      <p className="text-sm text-muted-foreground mt-1">Inicia sesión como alumno para ver sus nuevas sesiones y avisos.</p>
      <Button asChild className="mt-5 rounded-full"><Link to="/login?next=/mis-maestros">Ingresar</Link></Button>
    </div>
  );
  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="mis-maestros-page">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Mis Maestros</h1>
          <p className="text-sm text-muted-foreground mt-1">Lo nuevo de los Maestros que sigues. Te enviamos un resumen diario.</p>
        </div>
        {data?.maestros.length > 0 && <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => setManage((v) => !v)} data-testid="mis-maestros-manage">{manage ? "Ver novedades" : "Gestionar"}</Button>}
      </div>
      {!data ? <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        : manage ? <FavoriteMaestrosList />
        : data.maestros.length === 0 ? (
          <div className="text-center py-14 rounded-3xl border border-dashed" data-testid="mis-maestros-empty">
            <Users className="w-10 h-10 mx-auto text-primary/60 mb-3" />
            <p className="font-bold">Aún no sigues a ningún Maestro</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">Explora talleres, entra al perfil de un Maestro y pulsa «Seguir».</p>
            <Button asChild className="mt-5 rounded-full" data-testid="mis-maestros-explore"><Link to="/explorar"><Compass className="w-4 h-4 mr-1.5" />Explorar talleres</Link></Button>
          </div>
        ) : <Feed data={data} />}
      <div className="mt-8"><PushToggle /></div>
    </div>
  );
}
